import React, { useState, useRef } from 'react';
import SEO from "../common/SEO";
import HeaderOne from "../common/header/HeaderOne";
import FooterTwo from "../common/footer/FooterTwo";
import { useTranslation } from 'react-i18next';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { createContactFormMessage } from '../utils/contactFormMessageApi';

const FreeConsult = () => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        topic: '',
        email: '',
        consultationTime: '',
        additionalInfo: ''
    });
    const [showSuccess, setShowSuccess] = useState(false);
    const [token, setToken] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const captchaRef = useRef(null);

    // sitekey 本来就是公开的（要嵌进页面给 hCaptcha 用），放 env 只是为了
    // 换 key 不用改代码；取不到就退回线上这把，免得漏配变成整个表单不可用
    const captchaSiteKey = process.env.REACT_APP_HCAPTCHA_SITEKEY
        || '54443e30-488a-495a-abf4-acce12fab18c';

    const topics = [
        { value: 'feasibility', label: t('consult.topics.feasibility') },
        { value: 'planning', label: t('consult.topics.planning') },
        { value: 'construction', label: t('consult.topics.construction') },
        { value: 'portfolio', label: t('consult.topics.portfolio') },
        { value: 'custom', label: t('consult.topics.custom') }
    ];

    const onVerify = (token) => {
        setToken(token);
    };

    // hCaptcha 的 token 大约 2 分钟就过期，而这个表单有主题、期望时间、补充说明
    // 要填——先过验证码再慢慢写完，提交时手里那张票早就废了。过期/出错/关掉弹窗
    // 都要把 token 清掉，让按钮重新变灰，而不是留着一张死票去提交。
    const onTokenGone = () => setToken(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitting) return;

        if (!token) {
            alert(t("contact_form.please_verify_captcha"));
            return;
        }

        const topicLabel = topics.find((item) => item.value === formData.topic)?.label || formData.topic;

        // 「补充说明」是选填的，而主题、期望时间原先只进 ext_info——管理员那封
        // 提醒邮件只渲染 message，于是访客不写补充说明时正文就是空的：人家填好的
        // 信息全躺在后台没人看的字段里。拼进 message 才是完整的一条询盘，
        // 也顺带保证它不为空（后端 message 必填，空串会被打回「提交内容不完整」）。
        const message = [
            `Topic: ${topicLabel || '—'}`,
            formData.consultationTime ? `Preferred time: ${formData.consultationTime}` : '',
            formData.additionalInfo ? `\nNotes:\n${formData.additionalInfo}` : '',
        ].filter(Boolean).join('\n');

        // 字段对齐 ignition_research consultation 表单 → contact_form_message
        const payload = {
            fullname: formData.name,
            email: formData.email,
            message_subject: topicLabel || 'Free Consultation',
            message,
            // 之前 token 只在上面那句 if 里判了一下就丢掉，从没发给后端——
            // 也就没有任何一方真的去 hCaptcha 那边核验过。前端这道判断绕过它
            // 只需要直接 POST 接口，等于没有防护。真正的校验在服务端。
            captcha_token: token,
            page_info: typeof window !== 'undefined' ? window.location.href : '',
            ext_info: JSON.stringify({
                source_form: 'consultation_modal',
                form_type: 'free_consult',
                topic: topicLabel,
                consultation_time: formData.consultationTime,
            }),
        };

        setSubmitting(true);
        let res;
        try {
            res = await createContactFormMessage(payload);
        } finally {
            setSubmitting(false);
            // 无论成败都要重置：hCaptcha 的 token 是一次性的，服务端核验过就作废。
            // 失败后留着旧 token 的话，访客点「重试」发的是一张已经用掉的票，
            // 之后每次都被判成验证失败，表单从此再也提交不出去。
            captchaRef.current?.resetCaptcha();
            setToken(null);
        }

        if (res.ok) {
            setFormData({
                name: '',
                topic: '',
                email: '',
                consultationTime: '',
                additionalInfo: ''
            });
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 10000);
            return;
        }

        alert(res.message || t("contact_form.error_message"));
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <>
            <SEO title="Free Consultation - Cyberate Investments" />
            <main className="page-wrapper">
                <HeaderOne btnStyle="round" HeaderSTyle="header-not-transparent" />

                <div className="main-content page-content-text" style={{ 
                    padding: '100px 0', 
                    backgroundColor: '#000',
                    minHeight: '100vh'
                }}>
                    <div className="container">
                        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                            <h1 style={{ 
                                fontSize: '60px', 
                                color: '#fff', 
                                marginBottom: '20px', 
                                fontWeight: '700',
                                textAlign: 'center'
                            }}>
                                Real Estate Analysis
                            </h1>
                            <p style={{ 
                                color: '#ccc', 
                                fontSize: '18px', 
                                margin: '0 auto', 
                                lineHeight: '1.6', 
                                textAlign: 'center' 
                            }}>
                                {t('consult.subtitle')}
                            </p>
                        </div>

                        <div style={{ 
                            margin: '0 auto', 
                            padding: '40px', 
                            background: '#000', 
                            borderRadius: '12px', 
                            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
                            color: '#fff'
                        }}>
                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: '#fff', 
                                        fontWeight: '500' 
                                    }}>
                                        <span style={{ color: '#ff0000', marginRight: '4px' }}>*</span>
                                        {t('consult.form.name')}
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder={t('consult.form.namePlaceholder')}
                                        style={{
                                            width: '100%',
                                            height: '50px',
                                            padding: '0 15px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#fff',
                                            color: '#000'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: '#fff', 
                                        fontWeight: '500' 
                                    }}>
                                        <span style={{ color: '#ff0000', marginRight: '4px' }}>*</span>
                                        {t('consult.form.topic')}
                                    </label>
                                    <select
                                        name="topic"
                                        required
                                        value={formData.topic}
                                        onChange={handleChange}
                                        style={{
                                            width: '100%',
                                            height: '50px',
                                            padding: '0 15px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#fff',
                                            color: '#000',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="">{t('consult.form.selectPlaceholder')}</option>
                                        {topics.map(topic => (
                                            <option key={topic.value} value={topic.value}>
                                                {topic.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <p style={{ 
                                    color: '#fff', 
                                    fontSize: '16px', 
                                    marginBottom: '12px', 
                                    fontWeight: '500' 
                                }}>
                                    {t('consult.form.contactQuestion')}
                                </p>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        color: '#fff',
                                        fontWeight: '500'
                                    }}>
                                        <span style={{ color: '#ff0000', marginRight: '4px' }}>*</span>
                                        {t('consult.form.emailAddress')}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder={t('consult.form.emailPlaceholder')}
                                        style={{
                                            width: '100%',
                                            height: '50px',
                                            padding: '0 15px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#fff',
                                            color: '#000'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: '#fff', 
                                        fontWeight: '500' 
                                    }}>
                                        {t('consult.form.additionalInfoTitle')}
                                    </label>
                                    <textarea
                                        name="additionalInfo"
                                        value={formData.additionalInfo}
                                        onChange={handleChange}
                                        placeholder={t('consult.form.additionalInfoPlaceholder')}
                                        style={{
                                            width: '100%',
                                            padding: '15px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#fff',
                                            color: '#000',
                                            minHeight: '150px',
                                            lineHeight: '1.5',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <p style={{ 
                                    color: '#fff', 
                                    fontSize: '16px', 
                                    marginBottom: '12px', 
                                    fontWeight: '500' 
                                }}>
                                    {t('consult.form.scheduleQuestion')}
                                </p>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '8px', 
                                        color: '#fff', 
                                        fontWeight: '500' 
                                    }}>
                                        {t('consult.form.datetime')}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="consultationTime"
                                        value={formData.consultationTime}
                                        onChange={handleChange}
                                        style={{
                                            width: '100%',
                                            height: '50px',
                                            padding: '0 15px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#fff',
                                            color: '#000'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <HCaptcha
                                        sitekey={captchaSiteKey}
                                        onVerify={onVerify}
                                        onExpire={onTokenGone}
                                        onChalExpired={onTokenGone}
                                        onError={onTokenGone}
                                        ref={captchaRef}
                                    />
                                </div>

                                <p style={{ 
                                    color: '#ccc', 
                                    fontSize: '14px', 
                                    textAlign: 'center', 
                                    margin: '20px 0' 
                                }}>
                                    {t('consult.form.privacy')}
                                </p>

                                {!showSuccess ? (
                                    <button
                                        type="submit"
                                        disabled={!token || submitting}
                                        style={{
                                            background: 'linear-gradient(90deg, #FFAE2C 0%, #FFD945 100%)',
                                            color: '#000',
                                            padding: '15px 30px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '18px',
                                            // 没过验证码就点不了，而不是点了再弹一句「请先验证」
                                            cursor: (!token || submitting) ? 'not-allowed' : 'pointer',
                                            opacity: (!token || submitting) ? 0.5 : 1,
                                            width: '300px',
                                            fontWeight: '600',
                                            margin: '0 auto',
                                            display: 'block',
                                            transition: 'background-color 0.3s'
                                        }}
                                    >
                                        {t('consult.form.submit')}
                                    </button>
                                ) : (
                                    <div style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        padding: '20px',
                                        borderRadius: '6px',
                                        textAlign: 'center',
                                        width: '300px',
                                        margin: '0 auto',
                                        marginTop: '20px'
                                    }}>
                                        {t('consult.form.success')}
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>

                <FooterTwo />
            </main>
        </>
    );
};

export default FreeConsult;
